// ** Modules
import cookie from 'cookie';
import axios, { type AxiosResponse } from 'axios';
import { useMemo } from 'react';
import { signOut } from 'next-auth/react';
import { ErrorLink } from '@apollo/client/link/error';
import { SetContextLink } from '@apollo/client/link/context';
import {
	ServerError,
	LocalStateError,
	ServerParseError,
	UnconventionalError,
	CombinedGraphQLErrors,
	CombinedProtocolErrors
} from '@apollo/client/errors';
import {
	HttpLink,
	ApolloLink,
	// Observable,
	ApolloClient,
	InMemoryCache,
	type NormalizedCacheObject
} from '@apollo/client';

// ** Types
import type { AppProps } from 'next/app';
import type { AppContext } from '@/types/contextTypes';

type InitialState = NormalizedCacheObject | undefined;
type IInitializeApollo = {
	initialState?: InitialState | null;
	host: AppContext['host'];
	initialHeaders?: Record<string, string>;
};

// Environment variables
const isBeta = process.env.NODE_ENV !== 'production';

let globalApolloClient: ApolloClient | null = null;
let cachedAccessTokenPromise: Promise<string | undefined> | null = null;

const getAccessToken = () => {
	if (!cachedAccessTokenPromise) {
		cachedAccessTokenPromise = (async () => {
			try {
				const res = await fetch('/api/access-token', {
					credentials: 'include'
				});
				const data = await res.json();

				if (!res.ok) {
					if (res.status !== 401) {
						console.warn('Could not fetch token on client:', res.status, data);
					}
					return undefined;
				}

				return data.accessToken;
			} catch (err) {
				console.error('Error fetching token on client:', err);
				return undefined;
			}
		})();
	}
	return cachedAccessTokenPromise;
};

const createApolloClient = (host: AppContext['host'], initialHeaders?: Record<string, string>) => {
	const enhancedFetchAxios = async (input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> => {
		try {
			const url = typeof input === 'string' || input instanceof URL ? input.toString() : input.url;

			const body = typeof init.body === 'string' ? JSON.parse(init.body) : init.body;

			// Debugging console
			// console.groupCollapsed('🚀 [Apollo Request]');
			// console.log('➡️ URL:', url);
			// console.log('➡️ Method:', init.method);
			// console.log('➡️ Headers:', init.headers);
			// console.log('➡️ Body:', body);
			// console.groupEnd();

			const response: AxiosResponse<never> = await axios({
				url,
				method: init.method ?? 'POST',
				headers: {
					...(init.headers as Record<string, string>),
					'Content-Type': 'application/json'
				},
				data: body,
				withCredentials: true
			});

			const responseHeaders = new Headers();
			Object.entries(response.headers).forEach(([key, value]) => {
				if (typeof value === 'string') {
					responseHeaders.append(key, value);
				}
			});

			return new Response(JSON.stringify(response.data), {
				status: response.status,
				statusText: response.statusText,
				headers: responseHeaders
			});
		} catch (error) {
			if (axios.isAxiosError(error)) {
				const errorData = error.response?.data;

				return new Response(
					JSON.stringify(
						errorData || {
							errors: [{ message: error.message }]
						}
					),
					{
						status: 200,
						headers: { 'Content-Type': 'application/json' }
					}
				);
			}

			throw new Error(error instanceof Error ? error.message : 'Unexpected error');
		}
	};

	const httpLink = new HttpLink({
		uri: process.env.GRAPHQL_URL || process.env.NEXT_PUBLIC_GRAPHQL_URL || 'https://server.tischler.studio/graphql',
		credentials: 'include',
		fetchOptions: { mode: 'cors' },
		fetch: typeof window === 'undefined' ? enhancedFetchAxios : fetch
		// clientAwareness: {
		// 	name: isBeta ? 'TS-Portal-dev' : 'TS-Portal',
		// },
		// enhancedClientAwareness: {
		// 	transport: 'extensions'
		// }
	});

	const asyncAuthLink = new SetContextLink(async (prevContext, operation) => {
		const extraHeaders: Record<string, string> = {
			'x-client-environment': isBeta ? 'beta' : 'live',
			'x-operation-name': operation.operationName ?? 'Unknown'
		};

		const fortressKey =
			typeof window === 'undefined' ? process.env.INTERNAL_SECURITY_KEY : process.env.NEXT_PUBLIC_INTERNAL_SECURITY_KEY;

		if (fortressKey) {
			extraHeaders['x-fortress-key'] = fortressKey;
		}

		// Server-side (SSR)
		if (typeof window === 'undefined') {
			const rawCookie = prevContext?.headers?.cookie;

			if (rawCookie) {
				const parsedCookies = cookie.parse(rawCookie);
				const accessToken = parsedCookies.accessToken;

				if (accessToken) extraHeaders.authorization = `Bearer ${accessToken}`;
			}

			if (host) {
				try {
					const urlObj = new URL(host.startsWith('http') ? host : `https://${host}`);
					extraHeaders.Origin = urlObj.origin; // This automatically gets https://domain.com
				} catch {
					extraHeaders.Origin = `https://${host}`;
				}
			}

			return {
				headers: {
					...prevContext?.headers,
					...extraHeaders
				}
			};
		}

		// Client-side (CSR)
		let didLogTokenError = false;

		let accessToken: string | undefined;
		try {
			const fetchAccessToken = await getAccessToken();

			if (fetchAccessToken) {
				accessToken = fetchAccessToken;
			} else {
				didLogTokenError = true;
			}
		} catch (err) {
			if (!didLogTokenError) {
				console.error('Error fetching token on client:', err);
				didLogTokenError = true;
			}
		}

		return {
			headers: {
				...prevContext?.headers,
				...extraHeaders,
				...(typeof window === 'undefined' && initialHeaders?.origin ? { Origin: initialHeaders.origin } : {}),
				...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
				'x-operation-name': operation.operationName ?? 'Unknown'
			}
		};
	});

	const errorLink = new ErrorLink(({ error, operation /* forward */ }) => {
		// --- Configuration ---
		const reset = '\x1b[0m';
		const bold = '\x1b[1m';
		const red = '\x1b[31m';
		const yellow = '\x1b[33m';
		const cyan = '\x1b[36m';
		const gray = '\x1b[90m';
		const bgRed = '\x1b[41m\x1b[37m';

		const termWidth = process.stdout?.columns ? Math.min(process.stdout?.columns - 4, 60) : 60;
		const line = (char: string) => char.repeat(termWidth);

		const opName = operation.operationName || 'Unnamed Operation';
		const timestamp = new Date().toLocaleTimeString();

		// --- Vertical Space ---
		const forceSpace = `${gray} ${reset}`;
		console.error(`\n${forceSpace}`);

		// --- 1. Header ---
		console.error(`${gray}┌${line('─')}┐${reset}`);
		console.error(
			`${gray}│${reset} ${bgRed} ERROR ${reset} ${bold}${opName.padEnd(termWidth - 19)}${reset}${gray} ${timestamp} │${reset}`
		);
		console.error(`${gray}├${line('─')}┤${reset}`);

		// --- 2. Error Processing ---
		if (CombinedGraphQLErrors.is(error)) {
			error.errors.forEach(({ message, path, extensions }) => {
				const code = typeof extensions?.code === 'string' ? extensions.code : 'UNKNOWN';

				// Re-integrate the Schema Detection logic
				const isSchema =
					code === 'GRAPHQL_VALIDATION_FAILED' ||
					code.includes('VALIDATION') ||
					message.toLowerCase().includes('schema') ||
					message.toLowerCase().includes('cannot represent value');

				console.error(`  ${red}${bold}✖ [${code}]${reset} ${message}`);
				if (extensions?.report) {
					console.error(`  ${yellow}📋 REPORT:${reset} ${JSON.stringify(extensions.report, null, 2)}`);
				}

				if (path) console.error(`  ${cyan}📍 Path:${reset} ${gray}${path.join(' → ')}${reset}`);

				switch (code) {
					case 'UNAUTHENTICATED':
						cachedAccessTokenPromise = null;
						console.error(`  ${yellow}🔑 AUTH:${reset} Token cleared.`);
						break;
					case 'SESSION_EXPIRED':
						signOut({ callbackUrl: '/' });
						console.error(`  ${yellow}🚪 SESSION:${reset} Redirecting to login...`);
						break;
					case 'GRAPHQL_VALIDATION_FAILED':
						operation.setContext({});
						console.error(`  ${red}🧱 VALIDATION:${reset} Client/Server schema mismatch.`);
						break;
					case 'DATABASE_DOES_NOT_RESPOND':
						console.error(`  ${bgRed} 🗄️ DATABASE: ${reset} ${red}Down or unreachable.${reset}`);
						break;
					default:
						if (isSchema) console.error(`  ${red}🧱 SCHEMA:${reset} Contract mismatch detected.`);
				}

				if (isSchema && code !== 'GRAPHQL_VALIDATION_FAILED') {
					console.error(`  ${red}🧱 SCHEMA:${reset} Type or Enum contract mismatch detected.`);
				}
			});
		}
		// PROTOCOL ERRORS (CombinedProtocolErrors)
		else if (CombinedProtocolErrors.is(error)) {
			error.errors.forEach(({ message, extensions }) => {
				console.error(`  ${red}${bold}✖ [PROTOCOL ERROR]${reset} ${message}`);
				console.error(`  ${gray}Ext:${reset} ${JSON.stringify(extensions)}`);
			});
		}
		// NETWORK ERRORS (ServerError & ServerParseError)
		else if (ServerError.is(error) || ServerParseError.is(error)) {
			const statusCode = (error as any).statusCode;
			const body = (error as any).bodyText || '';

			// --- Expanded Network Detection ---
			if (error.message === 'Failed to fetch') {
				console.error(`  ${red}🔌 CONNECTION:${reset} Network unreachable.`);
				signOut();
			}

			if (body.includes('Not allowed by CORS')) {
				console.error(`  ${bgRed} CORS REJECTED ${reset} Origin not whitelisted.`);
				const originHeader = operation.getContext().headers?.Origin || 'Unknown';
				console.error(`  ${cyan}🔗 Origin:${reset} ${gray}${originHeader}${reset}`);
			} else if (statusCode === 429) {
				console.error(`  ${yellow}⏳ RATE LIMIT:${reset} Too many requests. Slow down.`);
			} else if (statusCode === 413) {
				console.error(`  ${yellow}📦 LARGE PAYLOAD:${reset} Request body exceeds server limits.`);
			} else if (statusCode >= 500) {
				const type = statusCode === 504 ? 'TIMEOUT' : 'CRASH';
				console.error(`  ${red}🔥 SERVER ${type}:${reset} (Status: ${statusCode})`);
			} else if (body.includes('<!DOCTYPE')) {
				console.error(`  ${red}✖ MALFORMED:${reset} Server returned HTML (likely a 404 or 500 page).`);
			} else {
				console.error(`  ${red}✖ ${ServerError.is(error) ? 'NETWORK' : 'PARSE'}:${reset} ${error.message}`);
			}
		}
		// LOCAL & UNCONVENTIONAL ERRORS
		else if (LocalStateError.is(error)) {
			console.error(`  ${cyan}🏠 LOCAL STATE:${reset} ${error.message}`);
		} else if (UnconventionalError.is(error)) {
			console.error(`  ${yellow}🌀 UNCONVENTIONAL:${reset} ${error.message}`);
		}
		// GENERIC FALLBACK
		else {
			console.error(`  ${red}✖ UNEXPECTED:${reset} ${error.constructor.name}: ${error.message}`);
		}

		// --- 3. Footer / Variables ---
		console.error(`${gray}├${line('╌')}┤${reset}`);
		const vars = JSON.stringify(operation.variables);
		const displayVars = vars.length > termWidth - 15 ? `${vars.substring(0, termWidth - 18)}...` : vars;
		console.error(`  ${gray}Variables: ${displayVars}${reset}`);
		console.error(`${gray}└${line('─')}┘${reset}`);

		console.error(`${forceSpace}\n`);
	});

	// ? Used for debugging requests
	// const logLink = new ApolloLink((operation, forward) => {
	// 	console.group(`[Apollo Request] ${operation.operationName || 'Unnamed'}`);
	// 	console.log('Variables:', operation.variables);
	// 	console.log('Context:', operation.getContext());
	// 	console.groupEnd();

	// 	return new Observable((observer) => {
	// 		const sub = forward(operation)?.subscribe({
	// 			next: (result) => {
	// 				console.group(
	// 					`[Apollo Response] ${operation.operationName || 'Unnamed'}`
	// 				);
	// 				console.log(result);
	// 				console.groupEnd();
	// 				observer.next(result);
	// 			},
	// 			error: (err) => {
	// 				console.error(
	// 					`[Apollo Error] ${operation.operationName || 'Unnamed'}`,
	// 					err
	// 				);
	// 				observer.error(err);
	// 			},
	// 			complete: observer.complete.bind(observer)
	// 		});

	// 		return () => {
	// 			if (sub) sub.unsubscribe();
	// 		};
	// 	});
	// });

	return new ApolloClient({
		ssrMode: typeof window === 'undefined',
		link: ApolloLink.from([
			// TODO retryLink,
			// ? logLink,
			errorLink,
			asyncAuthLink,
			httpLink
		]),
		cache: new InMemoryCache()
	});
};

export const initializeApollo = ({ initialState, host, initialHeaders }: IInitializeApollo) => {
	const _apolloClient =
		typeof window === 'undefined'
			? createApolloClient(host, initialHeaders)
			: (globalApolloClient ?? createApolloClient(host, initialHeaders));

	// Hydrate state
	if (initialState) {
		const existingCache = _apolloClient.extract();
		_apolloClient.cache.restore({
			...(typeof existingCache === 'object' && existingCache !== null ? existingCache : {}),
			...(typeof initialState === 'object' && initialState !== null ? initialState : {})
		});
	}

	if (typeof window !== 'undefined' && !globalApolloClient) {
		globalApolloClient = _apolloClient;
	}

	return _apolloClient;
};

export const useGetApolloClient = (pageProps: AppProps['pageProps']) => {
	const { apolloState, host, initialHeaders } = pageProps;

	const store = useMemo(
		() =>
			initializeApollo({
				initialState: apolloState,
				host: host || (typeof window !== 'undefined' ? window.location.host : undefined),
				initialHeaders
			}),
		[apolloState, host, initialHeaders]
	);

	return store;
};
